import React, { useState, useMemo } from "react";
import { ChevronDown, ChevronUp, MessageCircle, RotateCcw, BadgePercent, AlertCircle } from "lucide-react";
import { rupees, formatIndian, formatWeight } from "@/lib/utils";
import { shareToWhatsApp } from "@/lib/whatsappShare";

/**
 * Customer-facing discount calculator. Two modes:
 *   - flat: flat % off the MRP total
 *   - rate: override per-carat diamond rate; gold/labour/GST unchanged
 *
 * Receives pricing + rate-meta + base product; never reveals company/franchise costs.
 * Provides its own WhatsApp share with the discounted breakdown.
 */
export default function DiscountCalculator({ product, pricing, rateMeta }) {
  const [open, setOpen] = useState(false);
  const [method, setMethod] = useState("flat"); // 'flat' | 'rate'
  const [pctInput, setPctInput] = useState("");
  const [rateInput, setRateInput] = useState("");

  const gstRate = Number(rateMeta?.gst_rate ?? 0.03);
  const metalCost = Number(pricing?.metal_cost) || 0;
  const customerLabour = Number(pricing?.customer?.labour) || 0;
  const customerDiamond = Number(pricing?.customer?.diamond) || 0;
  const customerSubtotal = Number(pricing?.customer?.subtotal) || (metalCost + customerLabour + customerDiamond);
  const customerTotal = Number(pricing?.customer?.total) || 0;
  const totalCt = Number(pricing?.total_diamond_ct) || 0;
  const currentRatePerCt = Number(pricing?.customer?.avg_dia_rate) || (totalCt > 0 ? customerDiamond / totalCt : 0);

  const roundUp100 = (v) => Math.ceil(v / 100) * 100;

  const calc = useMemo(() => {
    if (method === "flat") {
      const pct = parseFloat(pctInput);
      if (isNaN(pct) || pctInput === "") return { valid: false, mrp: customerTotal, savings: 0 };
      if (pct < 0 || pct > 100) return { valid: false, error: "Discount must be between 0 and 100%", mrp: customerTotal, savings: 0 };
      const discountAmount = customerTotal * (pct / 100);
      const newMrp = roundUp100(customerTotal - discountAmount);
      return { valid: true, mode: "flat", pct, mrp: newMrp, savings: customerTotal - newMrp, discountAmount: customerTotal - newMrp };
    }
    // rate override
    const r = parseFloat(rateInput);
    if (isNaN(r) || rateInput === "") return { valid: false, mrp: customerTotal, savings: 0 };
    if (r <= 0) return { valid: false, error: "Per-carat rate must be greater than 0", mrp: customerTotal, savings: 0 };
    const newDiamondCost = totalCt * r;
    const newSubtotal = metalCost + customerLabour + newDiamondCost;
    const newGst = newSubtotal * gstRate;
    const newTotal = roundUp100(newSubtotal + newGst);
    const delta = customerTotal - newTotal;
    return {
      valid: true,
      mode: "rate",
      newRate: r,
      newDiamondCost,
      newSubtotal,
      newGst,
      mrp: newTotal,
      savings: delta, // positive = savings, negative = premium
    };
  }, [method, pctInput, rateInput, customerTotal, totalCt, metalCost, customerLabour, gstRate]);

  const isPremium = calc.valid && calc.savings < 0;
  const isDiscount = calc.valid && calc.savings > 0;

  const reset = () => { setPctInput(""); setRateInput(""); };

  const shareDiscount = () => {
    if (!calc.valid) return;
    const today = new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    const metalLabel = rateMeta?.metal_label || "Gold";
    const metalRate = rateMeta?.metal_rate || rateMeta?.gold_rate;
    const totalPcs = Number(pricing?.total_diamond_pcs) || 0;
    const gstPctStr = (gstRate * 100).toFixed(0);
    const ctStr = formatIndian(totalCt, { decimals: 3 });
    const header = [
      "*Ashth Pricing System*",
      "─────────────────────",
      `*Product:* ${product.jewel_code}`,
      `*Style:* ${product.style_no || ""}`,
      `*Category:* ${product.category || ""}${product.sub_category ? ` · ${product.sub_category}` : ""}`,
      `*Metal:* ${product.purity} ${metalLabel} (${formatWeight(product.net_weight)} g)`,
      `*Diamonds:* ${formatIndian(totalPcs)} pcs · ${ctStr} ct`,
      "",
      "─────────────────────",
      "*SPECIAL PRICE BREAKDOWN*",
      "",
    ];
    let body;
    if (calc.mode === "flat") {
      body = [
        `Original Price:    ${rupees(customerTotal)}`,
        `Discount (${calc.pct.toFixed(1)}%):  − ${rupees(calc.discountAmount)}`,
        "─────────────────────",
        `*Your Price:    ${rupees(calc.mrp)}*`,
        `_(You save ${rupees(calc.savings)})_`,
      ];
    } else {
      body = [
        `Metal (${product.purity}, ${formatWeight(product.net_weight)}g): ${rupees(metalCost)}`,
        `Labour: ${rupees(customerLabour)}`,
        `Diamond (${ctStr} ct): ${rupees(calc.newDiamondCost)}   _← Special rate_`,
        `Sub-total: ${rupees(calc.newSubtotal)}`,
        `GST (${gstPctStr}%): ${rupees(calc.newGst)}`,
        "─────────────────────",
        `*Your Price:    ${rupees(calc.mrp)}*`,
        calc.savings >= 0
          ? `_(Original: ${rupees(customerTotal)} · You save ${rupees(calc.savings)})_`
          : `_(Original: ${rupees(customerTotal)} · Additional charge ${rupees(-calc.savings)})_`,
        "",
        `_Diamond Rate: ${rupees(calc.newRate)}/ct_`,
      ];
    }
    const footer = [
      "",
      `_${metalLabel} Rate: ${rupees(metalRate)}/g_`,
      `_Price valid as of: ${today}_`,
    ];
    const text = [...header, ...body, ...footer].join("\n");
    shareToWhatsApp({ text, photoUrl: product?.photo_url || undefined });
  };

  return (
    <div className="luxe-card p-6" data-testid="discount-calc-panel">
      <button onClick={() => setOpen(!open)} data-testid="toggle-discount-calc" className="w-full flex items-center justify-between">
        <div className="flex items-center gap-3 text-left">
          <BadgePercent className="w-5 h-5 text-[#C9A84C]" strokeWidth={1.5} />
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#C9A84C]">Customer</p>
            <h3 className="font-serif text-2xl text-[#0D1B2A] mt-1">Calculate Customer Discount</h3>
          </div>
        </div>
        {open ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
      </button>

      {open && (
        <div className="mt-6 space-y-5">
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-500 mb-2">Discount method</p>
            <div className="flex flex-wrap gap-2">
              <label className={`px-4 py-2 rounded-md border cursor-pointer text-sm ${method === "flat" ? "border-[#C9A84C] bg-[#C9A84C]/10 text-[#0D1B2A]" : "border-[#0D1B2A]/15 text-slate-600"}`}>
                <input type="radio" name="discount-method" className="sr-only" checked={method === "flat"} onChange={() => setMethod("flat")} data-testid="discount-method-flat" />
                Flat Percentage Off
              </label>
              <label className={`px-4 py-2 rounded-md border cursor-pointer text-sm ${method === "rate" ? "border-[#C9A84C] bg-[#C9A84C]/10 text-[#0D1B2A]" : "border-[#0D1B2A]/15 text-slate-600"}`}>
                <input type="radio" name="discount-method" className="sr-only" checked={method === "rate"} onChange={() => setMethod("rate")} data-testid="discount-method-rate" />
                Override Per-Carat Diamond Rate
              </label>
            </div>
          </div>

          {method === "flat" ? (
            <div className="max-w-sm">
              <label className="text-xs uppercase tracking-wider text-slate-500 block mb-1.5">Discount %</label>
              <div className="relative">
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  placeholder="e.g. 5.0"
                  value={pctInput}
                  onChange={(e) => setPctInput(e.target.value)}
                  data-testid="discount-pct-input"
                  className="w-full h-11 px-3 pr-10 rounded-md border border-[#0D1B2A]/15 bg-white text-base focus:outline-none focus:border-[#C9A84C]"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">%</span>
              </div>
              {calc.error && <p className="text-xs text-red-600 mt-1.5 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{calc.error}</p>}
            </div>
          ) : (
            <div className="max-w-sm space-y-3">
              <div>
                <p className="text-xs uppercase tracking-wider text-slate-500 mb-1.5">Current Customer Rate</p>
                <p className="font-mono text-sm text-slate-700" data-testid="discount-current-rate">{rupees(currentRatePerCt)}/ct</p>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-500 block mb-1.5">New Per-Carat Rate (₹/ct)</label>
                <input
                  type="number"
                  step="100"
                  min="0"
                  placeholder="e.g. 25000"
                  value={rateInput}
                  onChange={(e) => setRateInput(e.target.value)}
                  data-testid="discount-rate-input"
                  className="w-full h-11 px-3 rounded-md border border-[#0D1B2A]/15 bg-white text-base focus:outline-none focus:border-[#C9A84C]"
                />
                {calc.error && <p className="text-xs text-red-600 mt-1.5 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{calc.error}</p>}
                {totalCt === 0 && <p className="text-xs text-amber-600 mt-1.5">This product has no diamonds — overriding the rate has no effect.</p>}
              </div>
            </div>
          )}

          <div className="border-t border-[#0D1B2A]/10 pt-4 space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-500 uppercase tracking-wider text-xs">Original Customer MRP</span>
              <span className="tabular-nums text-slate-700" data-testid="discount-original-mrp">{rupees(customerTotal)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[#C9A84C] uppercase tracking-wider text-xs font-medium">Discounted Price</span>
              <span className="font-serif text-2xl text-[#C9A84C] tabular-nums" data-testid="discount-new-mrp">{rupees(calc.valid ? calc.mrp : customerTotal)}</span>
            </div>
            {calc.valid && isDiscount && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-emerald-700 uppercase tracking-wider text-xs">You Save</span>
                <span className="tabular-nums text-emerald-700 font-medium" data-testid="discount-savings">{rupees(calc.savings)}</span>
              </div>
            )}
            {calc.valid && isPremium && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-amber-700 uppercase tracking-wider text-xs">Additional Charge</span>
                <span className="tabular-nums text-amber-700 font-medium" data-testid="discount-premium">{rupees(-calc.savings)}</span>
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={shareDiscount}
              disabled={!calc.valid}
              data-testid="discount-share-btn"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-[#25D366] hover:bg-[#1ebc59] disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium"
            >
              <MessageCircle className="w-4 h-4" strokeWidth={1.5} /> Share Discounted Price (WhatsApp)
            </button>
            <button
              onClick={reset}
              data-testid="discount-reset-btn"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md border border-[#0D1B2A]/15 text-slate-600 text-sm hover:bg-[#FAF8F3]"
            >
              <RotateCcw className="w-4 h-4" strokeWidth={1.5} /> Reset
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
